/**
 * 시스템 설정 — 「자동 메일」 탭(EA-79)·판 보기(EA-80)·새 판(EA-81).
 *
 * 요구: 사장님 2026-10-02 「통상적으로는 관리자가  메일 다섯가지에 대해서 관리 할 수 있게 하는가」 → 「1」(조정자 전달) —
 * 시스템 설정에 「자동 메일」 탭을 두고, 회원에게 저절로 나가는 메일 다섯 가지(가입 이메일 인증번호·아이디·비밀번호 찾기
 * 인증번호·OTP 재등록 인증번호·임시 비밀번호·가입 결과)의 제목·본문을 관리자가 판으로 고친다. 인증번호 같은 값 자리는
 * 시스템이 채운다. 서버는 가입 결과를 승인·거절 두 틀로 나눠 종류가 여섯이다(거래소 03 52번 ㉒ 제안).
 *
 * 계약: XM-119(목록, `kind`)·XM-120(종류마다 시행 중인 판)·XM-121(한 판) — 키 `terms.view`, XM-122(새 판) — 키
 * `terms.edit`(거래소백엔드 `AccountMailTemplateController`, bb11762). 다른 판 글(약관·지갑거래서명약관·고객센터 안내·
 * 거래 자료 메일)과 같은 방식이다 — 고치면 새 판, 옛 판은 지우지 않고, 고치기·지우기 창구는 없다. 1판 = 그때까지 나가던
 * 글(V530 — 서버가 넣음, 만든 관리자 없음).
 *
 * 영어 한 벌(사장님 2026-10-02 「일단 영어로만」). 관리자가 고치는 것은 제목·본문 글뿐이고, 값(인증번호·기한·아이디·임시
 * 비밀번호·관리자가 적은 사유)은 서버가 메일을 보낼 때 본문 아래 빈 줄 하나 뒤에 고정 줄(`fixedLines`)로 붙인다 — 이
 * 화면은 그 줄을 에디터 밖에 글로만 보인다(고칠 수 없음). 한글이 있으면 [Create]를 잠근다(서버도 400 `korean_not_allowed`).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAccountMailTemplate, getAccountMailTemplate, getEffectiveAccountMailTemplates, listAccountMailTemplates,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Field, Labeled, Notice, Row, TD, TextArea, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { effectiveMillis, Plain } from '@/components/Versioned'
import { DateTimeField } from '@/components/DateFields'
import { EnglishOnlyNote, hasKorean } from '@/components/EnglishOnly'
import { ACCOUNT_MAIL_KIND_LABEL, type AccountMailTemplate } from '@/api/types'

/** 탭 이름 — 「자동 메일」(조정자가 정한 탭 이름)을 영어로. */
export const ACCOUNT_MAIL = 'Automatic mail'

/** 칸 아래 안내·막힘 글(01 14-4 와 같은 꼴). */
const HINT = 'Automatic mails are sent to members in English. Write the subject and body in English.'
const BLOCKED = 'The subject or body has Korean letters. [Create] stays off until they are written in English only.'

function kindName(kind: string): string {
  return ACCOUNT_MAIL_KIND_LABEL[kind] ?? kind
}

/** XM-121 판 하나를 볼 때 고른 것. */
type Picked = { kind: string; version: number }

/** EA-79 — 자동 메일 탭(XM-120 종류 표 + 고른 종류의 판 목록 XM-119). */
export function AccountMailTemplatesTab() {
  const effective = useQuery({
    queryKey: ['account-mail-templates', 'effective'], queryFn: getEffectiveAccountMailTemplates,
  })
  const rows = effective.data ?? []
  // 처음에는 첫 종류의 판 목록을 보인다(서버 차례).
  const [picked, setPicked] = useState<string | null>(null)
  const kind = picked ?? rows[0]?.kind ?? null
  const [viewing, setViewing] = useState<Picked | null>(null)
  const effectiveOf = (k: string) => rows.find((r) => r.kind === k) ?? null

  return (
    <>
      <p className="mb-3 text-xs text-slate-500">
        The mails the exchange sends to a member by itself. Each kind is kept in versions: write the subject and body
        in English, and when the mail is sent the server adds the values (verification code, time limit, ID, temporary
        password, reason) as fixed lines below the body. A new version is used from its effective time. Old versions
        are not deleted.
      </p>
      <QueryState isPending={effective.isPending} error={effective.error} empty={rows.length === 0}
        emptyText="There are no automatic mail templates.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Kind</th><th className={TH}>In effect</th><th className={TH}>Subject</th>
            <th className={TH}>Effective at</th><th className={TH}>Fixed lines</th><th className={TH}>Versions</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.kind} className={t.kind === kind ? 'bg-slate-50 dark:bg-slate-800/60' : ''}>
                <td className={TD}><Labeled labels={ACCOUNT_MAIL_KIND_LABEL} code={t.kind} /></td>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    aria-label={`${kindName(t.kind)} v${t.version}`}
                    onClick={() => setViewing({ kind: t.kind, version: t.version })}>v{t.version}</button>
                </td>
                <td className={TD}>{t.subject}</td>
                <td className={TD}><Time ms={t.effectiveAt} /></td>
                <td className={`${TD} font-mono text-xs`}>
                  {t.fixedLines.map((l, i) => <div key={i}>{l}</div>)}
                </td>
                <td className={TD}>
                  {/* 고른 종류는 채운 단추 — 그 종류의 판 목록이 표 아래에 보인다. */}
                  <Button tone={t.kind === kind ? 'primary' : 'plain'} onClick={() => setPicked(t.kind)}>Versions</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {/* ⚠ 종류를 바꾸면 목록·새 판 창을 새로 — 한 종류의 판이 다른 종류 창구로 새지 않게. */}
      {kind !== null && (
        <KindVersions key={kind} kind={kind} effective={effectiveOf(kind)}
          onView={(version) => setViewing({ kind, version })} />
      )}
      {viewing !== null && (
        <ViewAccountMailDialog kind={viewing.kind} version={viewing.version}
          effective={effectiveOf(viewing.kind)?.version ?? null} onClose={() => setViewing(null)} />
      )}
    </>
  )
}

/** 고른 종류의 판 목록(XM-119 `kind`)과 「New version」(`terms.edit`). */
function KindVersions({ kind, effective, onView }: {
  kind: string
  effective: AccountMailTemplate | null
  onView: (version: number) => void
}) {
  const { has } = useSession()
  const list = useQuery({ queryKey: ['account-mail-templates', 'list', kind], queryFn: () => listAccountMailTemplates(kind) })
  const [creating, setCreating] = useState(false)
  // 판 새것부터(서버도 새 판 먼저 준다).
  const rows = [...(list.data ?? [])].filter((t) => t.kind === kind).sort((a, b) => b.version - a.version)
  const current = effective?.version ?? null
  const name = kindName(kind)

  return (
    <section className="mt-6" aria-label={`Versions of ${name}`}>
      <h3 className="mb-2 text-sm font-semibold">Versions of {name}</h3>
      {has('terms.edit') && (
        <div className="mb-3"><Button onClick={() => setCreating(true)}>New version</Button></div>
      )}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText="There are no versions of this kind.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Version</th><th className={TH}>Subject</th><th className={TH}>Effective at</th>
            <th className={TH}>Created by</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.version}>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    onClick={() => onView(t.version)}>v{t.version}</button>
                  {current === t.version && <Badge tone="ok">In effect</Badge>}
                </td>
                <td className={TD}>{t.subject}</td>
                <td className={TD}><Time ms={t.effectiveAt} /></td>
                {/* 1판은 서버 마이그레이션(V530)이 넣어 관리자가 없다. */}
                <td className={TD}><AdminRef adminId={t.createdByAdminId} empty="System" /></td>
                <td className={TD}><Time ms={t.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {creating && (
        <NewAccountMailDialog kind={kind} base={effective ?? rows[0] ?? null} onClose={() => setCreating(false)} />
      )}
    </section>
  )
}

/**
 * 서버가 본문 아래(빈 줄 하나 뒤)에 붙이는 고정 줄 — 글로만, 고칠 수 없다(XM-119 `fixedLines`). `{이름}` 자리는 보낼 때
 * 그 메일의 값으로 채워지고, 값이 없는 줄은 빠진다(예 가입 거절에 사유를 적지 않으면 Reason 줄 없음 — 계약 XM-119 표).
 */
function FixedLines({ lines }: { lines: readonly string[] }) {
  return (
    <section aria-label="Fixed lines">
      <h4 className="mb-1 text-xs font-semibold text-slate-500">Added below the body by the server (cannot be edited)</h4>
      <div className="rounded border border-slate-200 bg-slate-50 p-3 font-mono text-xs dark:border-slate-700 dark:bg-slate-800">
        {lines.length === 0
          ? <span className="text-slate-400">—</span>
          : lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Each {'{name}'} place is filled with the value of that mail when it is sent. A line without a value is left out.
      </p>
    </section>
  )
}

/** EA-80 — 판 보기(XM-121). 제목·본문·고정 줄은 글로만 — HTML 로 그리지 않는다. */
function ViewAccountMailDialog({ kind, version, effective, onClose }: {
  kind: string
  version: number
  effective: number | null
  onClose: () => void
}) {
  const q = useQuery({
    queryKey: ['account-mail-templates', kind, version], queryFn: () => getAccountMailTemplate(kind, version),
  })
  const t = q.data
  return (
    <ViewDialog open title={`${kindName(kind)} v${version}`} onClose={onClose}>
      <QueryState isPending={q.isPending} error={q.error}>
        {t && (
          <>
            <dl className="mb-3 grid grid-cols-[7rem_1fr] gap-1.5">
              <Row label="Kind"><Labeled labels={ACCOUNT_MAIL_KIND_LABEL} code={t.kind} /></Row>
              <Row label="Effective at">
                <Time ms={t.effectiveAt} />
                {effective === t.version && <Badge tone="ok">In effect</Badge>}
              </Row>
              <Row label="Created by"><AdminRef adminId={t.createdByAdminId} empty="System" /></Row>
              <Row label="Created at"><Time ms={t.createdAt} /></Row>
            </dl>
            <div className="mb-3 rounded border border-slate-200 p-3 dark:border-slate-700">
              <dl className="grid grid-cols-[7rem_1fr] gap-1.5">
                <Row label="Subject"><Plain text={t.subject} /></Row>
                <Row label="Body"><Plain text={t.body} /></Row>
              </dl>
            </div>
            <FixedLines lines={t.fixedLines} />
          </>
        )}
      </QueryState>
    </ViewDialog>
  )
}

/**
 * EA-81 — 새 판(XM-122 {kind, subject, body, effectiveAt?}). 처음 값은 시행 중 판의 제목·본문(다른 판 글과 같게). 고정
 * 줄은 에디터 밖에 글로만. 제목·본문 필수(공백만이면 잠김 — 서버 @NotBlank), 한글이 있으면 잠김(서버도 400), 시행 시각을
 * 읽을 수 없으면 잠김.
 */
function NewAccountMailDialog({ kind, base, onClose }: {
  kind: string
  base: AccountMailTemplate | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const w = useWrite()
  const [subject, setSubject] = useState(base?.subject ?? '')
  const [body, setBody] = useState(base?.body ?? '')
  const [effectiveAt, setEffectiveAt] = useState('')
  const name = kindName(kind)

  const { ms: effectiveMs, bad: badDate } = effectiveMillis(effectiveAt)
  const blank = subject.trim() === '' || body.trim() === ''
  const korean = hasKorean(subject) || hasKorean(body)

  async function submit() {
    const send = async () => {
      const t = await createAccountMailTemplate(kind, { subject, body }, effectiveMs)
      setFlash({ tone: 'ok', text: `Created ${name} v${t.version}.` })
      await qc.invalidateQueries({ queryKey: ['account-mail-templates'] })
    }
    await submitWrite(w, `${name} — new version`, send, onClose)
  }

  return (
    <Confirm open wide title={`${name} — new version`} action="Create" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={blank || korean || badDate}>
      <div className="flex flex-col gap-3">
        <Field label="Subject (required · 200 chars)" value={subject} maxLength={200}
          onChange={(e) => setSubject(e.target.value)} />
        <TextArea label="Body (required · 20,000 chars)" value={body} maxLength={20000} rows={8}
          onChange={(e) => setBody(e.target.value)} />
        <EnglishOnlyNote texts={[subject, body]} hint={HINT} blocked={BLOCKED} />
        <FixedLines lines={base?.fixedLines ?? []} />
        <DateTimeField label="Effective at (optional)" name="Effective at" value={effectiveAt} onChange={setEffectiveAt} />
        {badDate && <Notice tone="stop">The effective time cannot be read.</Notice>}
        <p className="text-xs text-slate-500">
          Saves as a new version. Old versions are not deleted. From the effective time, this kind of mail uses this
          version. Do not write the values in the body — the server adds them in the fixed lines.
        </p>
      </div>
    </Confirm>
  )
}
